import React, { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Upload,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  Users,
  Loader2,
  Lock,
  Sparkles,
  ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useNavigate, useLocation } from "react-router-dom";
import { getErrorMessage, isAuthExpiryError } from "@/utils/errorHandler";
import {
  getEmployeeLimitStatus,
  getEmployees,
  getCompanyMiscellaneousData,
  finalizeImportEmployees,
  validateImportEmployees,
} from "@/services/adminService";
import { downloadSampleTemplate } from "@/lib/utils";

const REQUIRED_PREVIEW_FIELDS = [
  { key: "employee_id", label: "Employee ID" },
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "gender", label: "Gender" },
  { key: "date_of_birth", label: "Date of Birth" },
  { key: "aadhar_number", label: "Aadhar Number" },
  { key: "pan_number", label: "PAN Number" },
  { key: "marital_status", label: "Marital Status" },
  { key: "address", label: "Address" },
  { key: "emergency_contact_name", label: "Emergency Contact Name" },
  { key: "emergency_contact_phone", label: "Emergency Contact Phone" },
  { key: "blood_group", label: "Blood Group" },
  { key: "designation", label: "Designation" },
  { key: "department", label: "Department" },
  { key: "date_of_joining", label: "Date of Joining" },
  { key: "work_location", label: "Work Location" },
  { key: "probation_end_date", label: "Probation End Date" },
  { key: "status", label: "Status" },

  // Bank (nested)
  { key: "bank_info.bank_name", label: "Bank Name" },
  { key: "bank_info.account_number", label: "Account Number" },
  { key: "bank_info.ifsc_code", label: "IFSC Code" },
  { key: "bank_info.branch", label: "Branch" },
];

const EmployeeImport = () => {
  const { hasFeature, planName, planId } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [importStep, setImportStep] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [importData, setImportData] = useState(null);
  const [conflictData, setConflictData] = useState(null);
  const [progress, setProgress] = useState(0);
  const [workingLocations, setWorkingLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designation, setDesignation] = useState([]);
  const [shiftsData, setShiftsData] = useState([]);
  const [isOverWriteAllowed, setIsOverWriteAllowed] = useState(false);
  const fileInputRef = useRef(null);

  const showDepartmentDesignation = hasFeature("department_mapping");

  useEffect(() => {
    getLocations();
  }, []);

  const getLocations = async () => {
    try {
      const response = await getCompanyMiscellaneousData();
      console.log("fetchCompanyMiscellaneousData", response);
      setWorkingLocations(
        response.work_locations.map((item) => item.name.toLowerCase().trim()),
      );
      setDepartments(
        response.departments.map((item) => item.name.toLowerCase().trim()),
      );
      setDesignation(
        response.designations.map((item) => item.name.toLowerCase().trim()),
      );
      setShiftsData(response.shifts);
    } catch (error) {
      console.error("Error fetching current working locations:", error);
      toast.error("Failed to load working locations");
    }
  };

  useEffect(() => {
    const checkLimit = async () => {
      try {
        const response = await getEmployeeLimitStatus();
        // If limit reached, show warning and redirect
        if (!response.allowed) {
          toast.error(response.message);
          setTimeout(() => navigate("/employees"), 2000);
        }
      } catch (error) {
        console.error("Error checking employee limit:", error);
      }
    };
    checkLimit();
  }, []);

  // Check if bulk import is enabled
  const canBulkImport = hasFeature("bulk_import");

  const columnMapping = {
    // Personal Information
    "Employee ID": "employee_id",
    Name: "name",
    Email: "email",
    Phone: "phone",
    Gender: "gender",
    "Date of Birth": "date_of_birth",
    "Aadhar Number": "aadhar_number",
    "PAN Number": "pan_number",
    "Marital Status": "marital_status",
    Address: "address",

    // // Emergency Contact
    "Emergency Contact Name": "emergency_contact_name",
    "Emergency Contact Phone": "emergency_contact_phone",
    "Blood Group": "blood_group",

    Status: "status",
    // Job Information
    Department: "department",
    Designation: "designation",
    Shift: "shift",
    "Date of Joining": "date_of_joining",
    "Work Location": "work_location",
    "Probation End Date": "probation_end_date",
    "Bank Name": "bank_name",
    "Account Number": "account_number",
    "IFSC Code": "ifsc_code",
    Branch: "branch",
  };

  if (!showDepartmentDesignation) {
    delete columnMapping.Department;
    delete columnMapping.Designation;
  }

  const baseRequiredFields = [
    "employee_id",
    "name",
    "email",
    "phone",
    "gender",
    "date_of_birth",
    "aadhar_number",
    "pan_number",
    "marital_status",
    "status",
    "address",
    "shift",
    "date_of_joining",
    "work_location",
    "bank_info.bank_name",
    "bank_info.account_number",
    "bank_info.ifsc_code",
    "bank_info.branch",
  ];

  const SummaryCard = ({ label, value, color }) => (
    <div className={`text-center p-3 rounded-lg bg-${color}-50`}>
      <p className={`text-xl font-bold text-${color}-600`}>{value}</p>
      <p className={`text-xs font-medium text-${color}-600`}>{label}</p>
    </div>
  );

  const requiredFields = showDepartmentDesignation
    ? [...baseRequiredFields, "department", "designation"]
    : baseRequiredFields;

  const getNested = (obj, path) =>
    path.split(".").reduce((o, k) => (o ? o[k] : undefined), obj);

  const validateEmployeeData = (employee, rowIndex, locations) => {
    const errors = [];

    // // Check required fields
    requiredFields.forEach((field) => {
      const val = field.includes(".")
        ? getNested(employee, field)
        : employee[field];
      if (!val || val.toString().trim() === "") {
        errors.push(` Missing required field - ${field}`);
      }
    });

    // Email validation
    if (employee.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(employee.email)) {
      errors.push(` Invalid email format - ${employee.email}`);
    }

    // Phone validation (only if phone is provided)
    if (employee.phone && employee.phone.toString().trim() !== "") {
      const cleanPhone = employee.phone.toString().replace(/\D/g, "");
      if (cleanPhone.length !== 10) {
        errors.push(
          ` Invalid phone number - ${employee.phone} (must be 10 digits)`,
        );
      }
    }

    // Date validations (only if dates are provided)
    if (
      employee.date_of_birth &&
      employee.date_of_birth.toString().trim() !== "" &&
      isNaN(Date.parse(employee.date_of_birth))
    ) {
      errors.push(` Invalid date of birth - ${employee.date_of_birth}`);
    }

    if (
      employee.date_of_joining &&
      employee.date_of_joining.toString().trim() !== "" &&
      isNaN(Date.parse(employee.date_of_joining))
    ) {
      errors.push(` Invalid date of joining - ${employee.date_of_joining}`);
    }

    // Gender validation (only if provided)
    if (
      employee.gender &&
      employee.gender.toString().trim() !== "" &&
      !["male", "female", "other"].includes(employee.gender)
    ) {
      errors.push(
        ` Invalid gender - ${employee.gender} (must be male, female, or other)`,
      );
    }

    // Marital status validation (only if provided)
    if (
      employee.marital_status &&
      employee.marital_status.toString().trim() !== "" &&
      !["single", "married", "divorced", "widowed"].includes(
        employee.marital_status,
      )
    ) {
      errors.push(` Invalid marital status - ${employee.marital_status}`);
    }

    if (
      employee.work_location &&
      employee.work_location.toString().trim() !== "" &&
      !workingLocations.includes(employee.work_location.toLowerCase())
    ) {
      errors.push(` Invalid Work Location - ${employee.work_location}`);
    }

    if (
      showDepartmentDesignation &&
      employee.department &&
      employee.department.toString().trim() !== "" &&
      !departments.includes(employee.department.toLowerCase())
    ) {
      errors.push(` Invalid Department - ${employee.department}`);
    }

    if (
      showDepartmentDesignation &&
      employee.designation &&
      employee.designation.toString().trim() !== "" &&
      !designation.includes(employee.designation.toLowerCase())
    ) {
      errors.push(` Invalid Designation - ${employee.designation}`);
    }

    if (
      employee.shift &&
      employee.shift.toString().trim() !== "" &&
      !shiftsData
        .map((shift) => shift.name.toLowerCase())
        .includes(employee.shift.toLowerCase())
    ) {
      errors.push(` Invalid Shift - ${employee.shift}`);
    }

    if (
      employee.pan_number &&
      !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(employee.pan_number)
    ) {
      // Validate PAN
      errors.push(` Invalid PAN format - ${employee.pan_number}`);
    }

    // Validate Aadhar
    if (
      employee.aadhar_number &&
      !/^[2-9]{1}[0-9]{3}[0-9]{4}[0-9]{4}$/.test(employee.aadhar_number)
    ) {
      errors.push(` Invalid Aadhar number format - ${employee.aadhar_number}`);
    }

    // Validate IFSC
    if (
      employee.bank_info.ifsc_code &&
      !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(employee.bank_info.ifsc_code)
    ) {
      errors.push(` Invalid IFSC code - ${employee.bank_info.ifsc_code}`);
    }
    // Validate Locations   workingLocations
    if (
      employee.work_location &&
      employee.work_location.toString().trim() !== "" &&
      ![...locations].includes(employee.work_location.toLowerCase())
    ) {
      errors.push(` Invalid Locations - ${employee.work_location}`);
    }
    // Numeric field validations (only if provided)
    return errors;
  };

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const getFieldValue = (obj, path) => {
    return path.split(".").reduce((acc, key) => acc?.[key], obj) ?? "";
  };

  const setNested = (obj, path, value) => {
    const parts = path.split(".");
    let curr = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      const k = parts[i];
      if (!curr[k] || typeof curr[k] !== "object") curr[k] = {};
      curr = curr[k];
    }
    curr[parts[parts.length - 1]] = value;
  };

  const parseExcelDate = (value) => {
    if (value === undefined || value === null || value === "") return null;

    if (typeof value === "number") {
      const date = new Date((value - 25569) * 86400 * 1000);
      return isNaN(date.getTime()) ? null : date.toISOString().split("T")[0];
    }

    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date.toISOString().split("T")[0];
  };

  const parseYesNoBoolean = (value) => {
    const v = (value ?? "").toString().trim().toLowerCase();
    if (!v) return undefined;
    return v === "yes" || v === "true" || v === "1";
  };

  const parseExcelData = async (data) => {
    const employees = [];
    const errors = []; // [{ row, field, message }]
    const conflicts = []; // server conflicts if you need
    let valid = true;

    // -------------------------------
    // 1) MAP EXCEL → EMPLOYEES (SYNC)
    // -------------------------------
    data.forEach((row, index) => {
      const employee = {};
      const excelRow = index + 2; // row 1 header

      Object.entries(columnMapping).forEach(([excelColumn, dbField]) => {
        const value = row?.[excelColumn];
        if (value === undefined || value === null || value === "") return;

        // dates
        if (
          dbField === "date_of_birth" ||
          dbField === "date_of_joining" ||
          dbField === "probation_end_date"
        ) {
          const parsed = parseExcelDate(value);
          if (parsed) setNested(employee, dbField, parsed);
          return;
        }

        // gender / marital_status
        if (dbField === "gender" || dbField === "marital_status") {
          setNested(employee, dbField, value.toString().toLowerCase().trim());
          return;
        }

        // boolean yes/no
        if (dbField === "is_on_probation") {
          const boolVal = parseYesNoBoolean(value);
          if (boolVal !== undefined) setNested(employee, dbField, boolVal);
          return;
        }

        // default string
        setNested(employee, dbField, value.toString().trim());
      });

      // meta
      employee._row = excelRow;

      // ensure bank_info exists
      employee.bank_info = {
        bank_name: employee.bank_info?.bank_name || employee.bank_name || "",
        account_number:
          employee.bank_info?.account_number || employee.account_number || "",
        ifsc_code: employee.bank_info?.ifsc_code || employee.ifsc_code || "",
        branch: employee.bank_info?.branch || employee.branch || "",
      };

      // delete only if you still might have flat columns
      delete employee.bank_name;
      delete employee.account_number;
      delete employee.ifsc_code;
      delete employee.branch;
      employees.push(employee);
    });

    // -------------------------------
    // 2) VALIDATE VIA API (ONCE)
    // -------------------------------
    let validationRes;
    try {
      validationRes = await validateImportEmployees(employees);
    } catch (error) {
      if (isAuthExpiryError(error)) throw error;
      throw new Error(getErrorMessage(error, "Employee import validation failed"));
    }
    // expected shape:
    // { valid: boolean, rows: [{row, errors:[{field,message}]}], conflicts: [] }

    valid = !!validationRes?.valid;

    (validationRes?.rows || []).forEach((r) => {
      (r?.errors || []).forEach((err) => {
        errors.push({
          row: r.row,
          field: err?.field ?? null,
          message: err?.message ?? String(err),
        });
      });
    });

    setConflictData(validationRes?.conflicts || []);
    console.log(errors);

    return { employees, errors, valid };
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // -------------------------------
    // FILE TYPE VALIDATION
    // -------------------------------
    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    if (!validTypes.includes(file.type)) {
      toast.error("Please select a valid Excel file (.xlsx or .xls)");
      return;
    }

    try {
      setImporting(true);
      setProgress(10);

      // -------------------------------
      // READ EXCEL FILE
      // -------------------------------
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      let jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

      setProgress(30);

      if (!jsonData || jsonData.length === 0) {
        toast.error("Excel file is empty or has no data");
        setImporting(false);
        setProgress(0);
        return;
      }

      // -------------------------------
      // CHECK EMPLOYEE LIMIT
      // -------------------------------
      let limitedJsonData = jsonData;

      try {
        const limitResponse = await getEmployeeLimitStatus(jsonData.length);
        const {
          allowed,
          plan_limit,
          purchased_seats,
          remaining_seats,
          current_count,
        } = limitResponse;

        if (!allowed) {
          toast.warning(
            plan_limit === "unlimited" || purchased_seats < plan_limit
              ? `Only ${remaining_seats} employee(s) can be imported based on your remaining seats.`
              : `Your plan allows ${plan_limit} employees. You already have ${current_count}. Upgrade your plan.`,
          );
          setImporting(false);
          setProgress(0);
          return;
        }
      } catch (err) {
        console.warn("Employee limit check failed, continuing import.");
      }

      setProgress(40);

      // -------------------------------
      // PARSE & VALIDATE DATA
      // -------------------------------
      const { employees, errors, conflicts } = await parseExcelData(
        limitedJsonData,
        workingLocations,
      );

      setConflictData(conflicts);
      setProgress(55);

      // -------------------------------
      // BUILD ROW-WISE PREVIEW
      // -------------------------------
      const previewRows = employees.map((emp) => {
        const rowErrors = errors.filter((e) => e.row === emp._row);

        return {
          row: emp._row,
          data: emp, // ⭐ ACTUAL IMPORT DATA
          status: rowErrors.length > 0 ? "invalid" : "valid",
          issues: rowErrors.map((e) => e),
        };
      });

      // -------------------------------
      // PREVIEW RESULT STATE
      // -------------------------------
      setImportResults({
        success: previewRows.every((r) => r.status === "valid"),
        total: previewRows.length,
        created: 0,
        updated: 0,
        processed: 0,
        rows: previewRows, // ⭐ WHAT UI WILL RENDER
      });

      setShowResults(true);
      setImporting(false);
      return;
    } catch (error) {
      console.error("File upload error:", error);
      toast.error("Failed to process Excel file");
    } finally {
      setImporting(false);
    }
  };

  const processImport = async () => {
    try {
      setImporting(true);
      const employees = importResults.rows.map((r) => ({
        ...r.data,
        _row: r.row,
      }));
      toast.info("Uploading file", {
        description: "Uploading file to docs…",
      });
      const finalize = await finalizeImportEmployees(
        employees,
        isOverWriteAllowed,
      );

      setImportResults((prev) => ({
        ...prev,
        total: employees.length,
        created: finalize.created,
        updated: finalize.updated,
        processed: finalize.created + finalize.updated,
        errors: [],
        success: true,
        // keep rows if you want, otherwise UI table will disappear
        rows: prev.rows,
      }));

      setShowResults(true);
      toast.success("Import completed!");

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setShowResults(false);
    } catch (err) {
      console.error(err);
      toast.error("Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleImportOnly = async () => {
    await processImport();
  };

  const handleValidateOnly = async () => {
    try {
      const payload = importResults.rows.map((r) => ({
        ...r.data,
        _row: r.row,
      }));

      const res = await validateImportEmployees(payload);

      // store conflicts in state (array)
      const conflicts = res?.conflicts || [];
      setConflictData(conflicts);

      // flatten row validation errors
      const errors = [];
      (res?.rows || []).forEach((r) => {
        (r?.errors || []).forEach((err) => {
          errors.push({
            row: r.row,
            field: err?.field ?? null,
            message: err?.message ?? String(err),
          });
        });
      });

      // build UI rows (validation-only status)
      const previewRows = payload.map((emp) => {
        const rowErrors = errors.filter((e) => e.row === emp._row);

        return {
          row: emp._row,
          data: emp,
          status: rowErrors.length > 0 ? "invalid" : "valid",
          issues: rowErrors, // already objects
        };
      });

      setImportResults({
        success: previewRows.every((r) => r.status === "valid"),
        total: previewRows.length,
        created: 0,
        updated: 0,
        processed: 0,
        rows: previewRows,
      });

      const invalidCount = previewRows.filter(
        (r) => r.status === "invalid",
      ).length;
      const conflictCount = conflicts.length;

      // ✅ If there are validation errors, stop here
      if (invalidCount > 0) {
        toast.error(
          `${invalidCount} row(s) have errors. Fix them and validate again.`,
        );
        return;
      }

      // ✅ No validation errors: handle conflicts
      if (conflictCount > 0) {
        setShowConfirmDialog(true);
        setShowResults(false);
        toast.message(`${conflictCount} conflict(s) found`, {
          description:
            "These records already exist. Confirm how you want to proceed.",
        });
        return;
      }
      // else{
      processImport();
      setShowResults(false);
      // }

      // ✅ No validation errors and no conflicts
    } catch (e) {
      console.error(e);
      toast.error("Validation failed");
    }
  };

  const setDeepValue = (obj, path, value) => {
    const keys = path.split(".");
    const lastKey = keys.pop();

    // clone root (array/object safe)
    const root = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };

    let cur = root;

    for (const k of keys) {
      const next = cur?.[k];

      // clone each level (or create missing level)
      const cloned =
        next && typeof next === "object"
          ? Array.isArray(next)
            ? [...next]
            : { ...next }
          : {};

      cur[k] = cloned;
      cur = cloned;
    }

    cur[lastKey] = value;
    return root;
  };

  const handleInlineEdit = (rowIndex, field, value) => {
    setImportResults((prev) => {
      const updatedRows = prev.rows.map((r, idx) => {
        if (idx !== rowIndex) return r;

        const updatedData = setDeepValue(r.data, field, value);

        return {
          ...r,
          data: updatedData,
        };
      });

      return {
        ...prev,
        rows: updatedRows,
      };
    });
  };

  return (
    <div className="space-y-6" data-testid="import-employee">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate("/employees")}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Employee Import
          </h1>
          <p className="text-gray-500">
            Import employees from Excel file with validation and conflict
            resolution
          </p>
        </div>
      </div>
      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Upload className="h-5 w-5" />
            <span>Upload Excel File</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-center w-full">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <label
                    htmlFor={canBulkImport ? "file-upload" : undefined}
                    onClick={() => {
                      if (!canBulkImport) {
                        navigate("/subscription", {
                          state: {
                            from: location.pathname,
                            requiredFeature: "bulk_employee_import",
                            currentPlan: planName,
                            currentPlanId: planId,
                          },
                        });
                      }
                    }}
                    className={`flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg ${
                      canBulkImport
                        ? "cursor-pointer bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800"
                        : "cursor-pointer bg-gray-100 dark:bg-gray-900 opacity-60"
                    }`}
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      {!canBulkImport && (
                        <Lock className="w-10 h-10 mb-3 text-gray-400" />
                      )}
                      {canBulkImport && (
                        <FileText className="w-10 h-10 mb-3 text-gray-400" />
                      )}
                      <p className="mb-2 text-sm text-gray-500 flex items-center gap-2">
                        {canBulkImport ? (
                          <>
                            <span className="font-semibold">
                              Click to upload
                            </span>{" "}
                            employee Excel file
                          </>
                        ) : (
                          <>
                            <Lock className="w-4 h-4" />
                            <span className="font-semibold">
                              Bulk Import Locked
                            </span>
                          </>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">
                        {canBulkImport
                          ? "Excel files only (.xlsx, .xls)"
                          : `Available in Professional Plan`}
                      </p>
                      {!canBulkImport && (
                        <div className="mt-3">
                          <Badge className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                            <Sparkles className="w-3 h-3 mr-1" />
                            Upgrade to Unlock
                          </Badge>
                        </div>
                      )}
                    </div>
                    {canBulkImport && (
                      <input
                        id="file-upload"
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        accept=".xlsx,.xls"
                        onChange={handleFileUpload}
                        disabled={importing}
                      />
                    )}
                  </label>
                </TooltipTrigger>
                {!canBulkImport && (
                  <TooltipContent>
                    <p className="font-semibold">
                      Bulk Import is a Professional feature
                    </p>
                    <p className="text-xs">
                      Upgrade to Professional or Enterprise plan to import
                      multiple employees at once
                    </p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>

          {importing && (
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Processing import...
                </span>
                <span className="text-sm text-gray-600">
                  {Number(progress).toFixed(progress % 1 === 0 ? 0 : 2)}%
                </span>
              </div>
              <Progress value={progress} className="w-full" />
            </div>
          )}

          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              onClick={() =>
                downloadSampleTemplate(hasFeature("department_mapping"))
              }
            >
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
            <p className="text-sm text-gray-500">
              Use the template to ensure correct format
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              <span>Existing Employees Found</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {conflictData?.length} employee(s) already exist in the system.
                Do you want to overwrite their information with the Excel data?
              </AlertDescription>
            </Alert>

            {conflictData && (
              <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-gray-200 dark:border-gray-700">
                      <TableHead className="text-gray-700 dark:text-gray-300">
                        Employee ID
                      </TableHead>
                      <TableHead className="text-gray-700 dark:text-gray-300">
                        Name
                      </TableHead>
                      {showDepartmentDesignation && (
                        <TableHead>Department</TableHead>
                      )}
                      {showDepartmentDesignation && (
                        <TableHead>Designation</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {conflictData.map((employee, index) => (
                      <TableRow
                        key={index}
                        className="border-b border-gray-200 dark:border-gray-700"
                      >
                        <TableCell className="font-medium text-gray-900 dark:text-gray-200">
                          {employee.employee_id}
                        </TableCell>
                        <TableCell className="text-gray-800 dark:text-gray-300">
                          {employee.name}
                        </TableCell>
                        <TableCell className="text-gray-800 dark:text-gray-300">
                          {employee.department}
                        </TableCell>
                        <TableCell className="text-gray-800 dark:text-gray-300">
                          {employee.designation}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            <div className="flex items-center gap-3">
              <Checkbox
                id="terms"
                checked={isOverWriteAllowed}
                onCheckedChange={() =>
                  setIsOverWriteAllowed(!isOverWriteAllowed)
                }
              />
              <Label htmlFor="terms">Overwrite existing records</Label>
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowConfirmDialog(false);
                  setImportData(null);
                  setConflictData(null);
                  resetFileInput();
                }}
              >
                Cancel Import
              </Button>
              <Button
                onClick={() => {
                  setShowConfirmDialog(false);
                  processImport();
                }}
                disabled={importing}
                className="flex-1"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : isOverWriteAllowed ? (
                  "Confirm & Overwrite"
                ) : (
                  "Import New Records Only"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Results Dialog */}
      <Dialog open={showResults} onOpenChange={setShowResults}>
        <DialogContent className="max-w-6xl p-0 flex flex-col">
          {/* ================= HEADER ================= */}
          <DialogHeader className="px-6 py-2 border-b bg-background shrink-0">
            <DialogTitle className="flex items-center gap-2 text-base">
              {importResults?.success && importResults?.errors?.length === 0 ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600" />
              )}
              <span>Import Preview</span>
            </DialogTitle>
          </DialogHeader>

          {/* ================= SUMMARY ================= */}
          <div className="px-6 py-4 border-b bg-muted/30 shrink-0">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <SummaryCard
                label="Total"
                value={importResults?.total}
                color="blue"
              />
              <SummaryCard
                label="Created"
                value={importResults?.created}
                color="green"
              />
              <SummaryCard
                label="Updated"
                value={importResults?.updated}
                color="yellow"
              />
              <SummaryCard
                label="Errors"
                value={importResults?.errors?.length ?? 0}
                color="red"
              />
            </div>
          </div>

          {/* ================= SCROLLABLE TABLE ================= */}
          <div className="flex-1 overflow-y-auto max-h-[60vh] px-6 py-2">
            {importResults?.rows?.length > 0 && (
              <div className="border rounded-xl overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead className="sticky top-0 z-10 bg-muted">
                    <tr className="border-b">
                      <th className="px-3 py-2 text-left">Row</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      {REQUIRED_PREVIEW_FIELDS.map((f) => (
                        <th
                          key={f.key}
                          className="px-3 py-2 text-left whitespace-nowrap"
                        >
                          {f.label}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {importResults.rows.map((r, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className="border-b align-top hover:bg-muted/40"
                      >
                        <td className="px-3 py-2 font-mono text-xs">
                          #{rowIndex + 1}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-semibold
                        ${
                          r.status === "valid"
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                          >
                            {r.status.toUpperCase()}
                          </span>
                        </td>
                        {REQUIRED_PREVIEW_FIELDS.map((field) => {
                          const value = getFieldValue(r.data, field.key);
                          const fieldError = (r.issues || []).find(
                            (i) => i.field === field.key,
                          );

                          return (
                            <td
                              key={field.key}
                              className="px-3 py-2 min-w-[200px]"
                            >
                              <input
                                value={value}
                                onChange={(e) =>
                                  handleInlineEdit(
                                    rowIndex,
                                    field.key,
                                    e.target.value,
                                  )
                                }
                                className={`w-full px-2 py-2 rounded border text-sm
                            ${
                              fieldError
                                ? "border-red-500 bg-red-50"
                                : "border-slate-300"
                            }`}
                              />
                              {fieldError && (
                                <p className="text-[10px] text-red-600 mt-0.5">
                                  {fieldError.message}
                                </p>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ================= FOOTER =================   */}
          <div className="px-6 py-4 border-t bg-background flex justify-end gap-3 shrink-0">
            <Button
              variant="outline"
              onClick={() => {
                setShowResults(false);
                resetFileInput();
              }}
            >
              Close
            </Button>

            <Button
              // disabled={!importResults?.rows?.some(r => r.status === "valid")}
              onClick={handleValidateOnly}
              className="bg-primary"
            >
              Validate Records
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>Import Instructions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold mb-3 text-green-600">
                ✅ Required Fields
              </h3>
              <ul className="space-y-1 text-sm">
                <li>• Employee ID (unique identifier)</li>
                <li>• Name</li>
                <li>• Email</li>
                <li>• Phone</li>
                <li>• Gender</li>
                <li>• Date Of Birth</li>
                <li>• Aadhar Number</li>
                <li>• PAN Number</li>
                <li>• Marital Status</li>
                <li>• Address</li>
                <li>• Designation</li>
                <li>• Department</li>
                <li>• Date of Joining</li>
                <li>• Work Location</li>
                <li>• Status</li>
                <li>• Bank Name</li>
                <li>• Account Number</li>
                <li>• IFSC Code</li>
                <li>• Branch</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-3 text-blue-600">
                📋 Import Features
              </h3>
              <ul className="space-y-1 text-sm">
                <li>• Automatic validation and error reporting</li>
                <li>• Conflict detection for existing employees</li>
                <li>• Confirmation before overwriting data</li>
                <li>• Detailed import results and error logs</li>
                {/* <li>
                  • Support for all employee fields including salary structure
                </li> */}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeeImport;
